// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAggregationData } from '@shared/advisory/aggregation/providers';
import { useCallback, useMemo, useState } from 'react';
import { exportTableToExcel } from '@shared/ui-kit/utils';
import { useStateValue } from '@shared/ui-kit';
import { buildAccountBreakdown } from '@shared/advisory/aggregation/utils/build-account-breakdown';
import { TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import { useHandlerManageTags } from '@shared/tag-management/hooks/use-handler-manage-tags';
import { useAppDispatch } from '@advisor-app/store/store';
import { portfolioPositionsSlice } from '@shared/data-access/slices/portfolio-positions';
import {
  resetRebalanceConfig,
  updateRebalanceConfig,
} from '@advisor-app/store/slices/parameters';
import { setWorkflow } from '@advisor-app/store/slices/workflow';
import { useNavigate } from 'react-router';
import { type Paths } from '@shared/data-access/interfaces/routes/paths';
import { setComparisonTickers } from '@advisor-app/store/slices/my-research';
import { useAssetOperations } from './use-asset-operations';
import { type TargetAssetState } from '@shared/data-access/interfaces/api/ai/target-strategy';
import { useTranslation } from 'react-i18next';
import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import { type WorkflowState } from '@shared/data-access/interfaces/redux/workflow-state';

export const usePositions = () => {
  const { t } = useTranslation(['common', 'components']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    status,
    historicalBalances,
    hasHistoricalBalances,
    periodPicker,
    currency,
    institutionId,
    onRefresh,
    accountsPositions,
    hasHoldings,
  } = useAggregationData();

  const { onTransfer, onBuy, onSell, portfolioFunds, canUseTrader } =
    useAssetOperations();

  const [viewMode, setViewMode] = useStateValue<'market' | 'product-family'>(
    'aggregator-positions-view-mode',
    'market',
  );

  const holdings = useMemo(() => {
    switch (viewMode) {
      case 'market':
        return accountsPositions.assetClassHoldings;
      case 'product-family':
        return accountsPositions.assetTypeHoldings;
    }
  }, [accountsPositions, viewMode]);

  const [openTagsModal, setOpenTagsModal] = useState(false);

  const handleOpenModal = useCallback(() => {
    setOpenTagsModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenTagsModal(false);
  }, []);

  const {
    onChangeTag,
    selectTag,
    tagNames,
    tags,
    positions,
    tickerTags,
    onCreateTag,
    onCreateTickerTag,
    suggestions,
  } = useHandlerManageTags({
    feature: TickerTagFeature.AggregatorPositions,
    dataSource: holdings,
  });

  const defaultText = t('common.select-tag', { defaultValue: 'Select tag' });

  const optionsAvailable = useMemo(
    () => [defaultText, ...tagNames],
    [defaultText, tagNames],
  );

  const breakdowns = useMemo(
    () => buildAccountBreakdown(accountsPositions.holdings),
    [accountsPositions.holdings],
  );

  const onDownload = useCallback((data: AccountsHoldingsData) => {
    try {
      exportTableToExcel(
        data.subRows.map(subRow => ({
          'Asset class': subRow.asset_class,
          'Asset type': subRow.asset_type,
          Name: subRow.name,
          Ticker: subRow.ticker,
          ISIN: subRow.isin,
          Currency: subRow.currency,
          Quantity: subRow.quantity,
          Price: subRow.price,
          Investment: subRow.investment,
        })),
        `Investment positions.xlsx`,
      );
    } catch (error) {
      console.error(error);
      return error;
    }
  }, []);

  const onRebalance = useCallback(
    (flow: WorkflowState, data: AccountsHoldingsData) => {
      dispatch(resetRebalanceConfig());
      dispatch(updateRebalanceConfig({ inputType: 'amount' }));
      dispatch(setWorkflow(flow));

      const portfolioPositions = data.subRows.filter(
        ({ ticker, isin }) => ticker !== null || isin !== null,
      );

      const currentPortfolioPositions = portfolioPositions.map(asset => ({
        price: asset.price,
        qty: asset.quantity,
        ticker: asset.ticker ?? asset.isin ?? '',
        unrealized_capital_gains: 0,
        harvesting: {
          percentage: 0,
          status: 0,
          replacement: null,
        },
        details: asset.details ?? undefined,
        account_id: asset.iban!,
        institution_id: asset.institution_id,
        institution_name: asset.institution_name,
      }));

      dispatch(
        portfolioPositionsSlice.actions.setCurrentPortfolioPositions(
          currentPortfolioPositions,
        ),
      );

      const totalInvestment = portfolioPositions.reduce(
        (sum, position) => sum + position.quantity * position.price,
        0,
      );

      const targetAsset: TargetAssetState[] = portfolioPositions
        .map(position => ({
          ticker: position.ticker ?? position.isin ?? '',
          weight:
            Math.round(
              ((position.quantity * position.price) / totalInvestment) * 100 * 1000,
            ) / 1000,
          details: position.details ?? undefined,
          isin: position.details?.isin ?? position.isin ?? undefined,
          name: position.details?.name ?? position.name,
          currency: null,
          account_id: position.iban!,
          institution_id: position.institution_id,
          institution_name: position.institution_name,
        }))
        .filter(asset => asset.weight > 0)
        .sort((a, b) => b.weight - a.weight);

      dispatch(
        portfolioPositionsSlice.actions.setTargetPortfolioPositions(targetAsset),
      );

      navigate('/rebalance/configuration' as Paths);
    },
    [dispatch, navigate],
  );

  const onLoad = useCallback(
    (flow: WorkflowState, data: AccountsHoldingsData) => {
      if (flow.type === 'rebalance') {
        onRebalance(flow, data);
        return;
      }

      dispatch(setWorkflow(flow));

      const currentPortfolioPositions = data.subRows
        .filter(({ ticker, isin }) => ticker !== null || isin !== null)
        .map(asset => ({
          price: asset.price,
          qty: asset.quantity,
          ticker: asset.ticker ?? asset.isin ?? '',
          unrealized_capital_gains: 0,
          harvesting: {
            percentage: 0,
            status: 0,
            replacement: null,
          },
        }));

      dispatch(
        portfolioPositionsSlice.actions.setCurrentPortfolioPositions(
          currentPortfolioPositions,
        ),
      );

      navigate(
        (flow.type === 'tax-harvesting'
          ? `/tax-manager/${flow.asset}`
          : `/analysis/${flow.asset}`) as Paths,
      );
    },
    [dispatch, navigate, onRebalance],
  );

  const onComparison = useCallback(
    (data: AccountsHoldingsData) => {
      const tickers = data.subRows
        .map(({ ticker, isin, details }) => ticker ?? details?.ticker ?? isin)
        .filter(Boolean) as string[];

      dispatch(setComparisonTickers(tickers));

      navigate({
        pathname: '/research',
        search: '?tab=comparison',
      });
    },
    [dispatch, navigate],
  );

  return {
    holdings,
    breakdowns,
    hasHoldings,
    status,
    historicalBalances,
    hasHistoricalBalances,
    onDownload,
    periodPicker,
    currency,
    institutionId,
    onRefresh,
    viewMode,
    setViewMode,

    // tag management
    onChangeTag,
    selectTag,
    tagNames,
    tags,
    positions,
    tickerTags,
    onCreateTag,
    onCreateTickerTag,
    suggestions,
    defaultText,
    optionsAvailable,
    openTagsModal,
    handleOpenModal,
    handleCloseModal,

    // portfolio analysis
    onLoad,
    onComparison,
    onTransfer,
    onBuy,
    onSell,
    portfolioFunds,
    canUseTrader,
  } as const;
};
